import { auth, PROXY_URL } from './firebase';

export interface GenerateResponse {
  imageBase64: string;
  mimeType: string;
  cost: number;
  latencyMs: number;
  requestId: string;
}

export interface HistoryItem {
  id: string;
  prompt: string;
  thumbBase64?: string;
  mimeType: string;
  cost: number;
  createdAt: number;
  shopify?: { fileGid: string; productId: string | null; pushedAt: number } | null;
}

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const token = await user.getIdToken();
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  headers.set('Content-Type', 'application/json');
  return fetch(`${PROXY_URL}${path}`, { ...init, headers });
}

export interface ReferenceImage { mime_type: string; data: string; }

// Turn a proxy/bridge error into something a human can act on, instead of
// surfacing raw "Proxy 502: {json}". The proxy now propagates {code,message}
// with a meaningful status (422 blocked, 429 rate-limit).
function friendlyError(status: number, payload: { code?: string; message?: string; error?: string } | null): string {
  const code = payload?.code ?? payload?.error;
  if (code === 'VERTEX_BLOCKED_CONTENT' || status === 422) {
    return 'The content-safety filter blocked this request. It usually triggers when editing photos of real people (faces/identity) or with sensitive content — try a different image or rephrase the prompt.';
  }
  if (code === 'RESOURCE_EXHAUSTED' || status === 429) {
    return 'The image service is busy right now (rate limit). Wait a minute and try again.';
  }
  if (payload?.message) return payload.message;
  return `Image generation failed (HTTP ${status}). Please try again.`;
}

export async function generateImage(prompt: string, referenceImages: ReferenceImage[] = []): Promise<GenerateResponse> {
  const res = await authedFetch('/generate', {
    method: 'POST',
    body: JSON.stringify({
      prompt,
      reference_images: referenceImages,
      resolution: '1K',
      aspect_ratio: '1:1',
      num_images: 1,
    }),
  });
  if (!res.ok) {
    let payload: { code?: string; message?: string; error?: string } | null = null;
    try { payload = await res.json(); } catch { payload = null; }
    throw new Error(friendlyError(res.status, payload));
  }
  return res.json();
}

export async function listHistory(): Promise<HistoryItem[]> {
  const res = await authedFetch('/history?limit=20', { method: 'GET' });
  if (!res.ok) throw new Error(`History ${res.status}`);
  const data = await res.json();
  return data.items;
}

export interface UserRow { email: string; role: 'admin' | 'user'; addedAt: number; }
export interface UsageRow { email: string; totalGenerations: number; totalCost: number; }

export async function listUsers(): Promise<UserRow[]> {
  const res = await authedFetch('/admin/users', { method: 'GET' });
  if (!res.ok) throw new Error(`Users ${res.status}`);
  const data = await res.json();
  return data.users;
}

export async function listUsage(): Promise<UsageRow[]> {
  const res = await authedFetch('/admin/usage', { method: 'GET' });
  if (!res.ok) throw new Error(`Usage ${res.status}`);
  const data = await res.json();
  return data.usage;
}

export async function addUser(email: string, role: 'admin' | 'user'): Promise<void> {
  const res = await authedFetch('/admin/users', {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  });
  if (!res.ok) throw new Error(`Add user ${res.status}: ${await res.text()}`);
}

// A failed revocation must READ as a failure ([BUG] 91AkTDW4): the proxy now
// answers 404 when the address is not on the allowlist instead of `{ok:true}`,
// so surface the proxy's own sentence rather than a raw JSON dump.
export async function removeUser(email: string): Promise<void> {
  const res = await authedFetch(`/admin/users/${encodeURIComponent(email)}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Remove user failed (HTTP ${res.status}).`));
}

// Admin invite ([PHASE 2] XDKuaRjc): allowlists the email + creates its Auth
// account server-side. The returned tempPassword is shown to the admin exactly
// once and never persisted anywhere.
export async function inviteUser(email: string): Promise<{ tempPassword: string }> {
  const res = await authedFetch('/admin/users/invite', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Invite failed (HTTP ${res.status}).`));
  return res.json();
}

// ----- Push-to-Shopify ([PHASE 3] Ft8sVkDJ, admin-only) -----

export interface ShopifyProduct { id: string; title: string; status: string; }

export interface PushToShopifyResult {
  fileGid: string;
  fileStatus: string;
  productMediaId: string | null;
  /** Set when the file landed in Shopify Files but the optional product attach failed. */
  productAttachError: string | null;
  adminFilesUrl: string;
}

async function readErrorMessage(res: Response, fallback: string): Promise<string> {
  let payload: { message?: string; error?: string } | null = null;
  try { payload = await res.json(); } catch { payload = null; }
  return payload?.message ?? payload?.error ?? fallback;
}

export async function listShopifyProducts(): Promise<ShopifyProduct[]> {
  const res = await authedFetch('/shopify/products', { method: 'GET' });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Could not load products (HTTP ${res.status}).`));
  const data = await res.json();
  return data.products;
}

export async function pushToShopify(params: { generationId: string; imageBase64?: string; productId?: string }): Promise<PushToShopifyResult> {
  const res = await authedFetch('/shopify/push-image', {
    method: 'POST',
    body: JSON.stringify(params),
  });
  if (!res.ok) throw new Error(await readErrorMessage(res, `Push to Shopify failed (HTTP ${res.status}).`));
  return res.json();
}
